import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/feature/Pagination";
import { Breadcrumb } from "@/components/feature/Breadcrumb";
import { allWorks } from "@/mocks/works";
import { newsList } from "@/mocks/news";
import { WORK_TYPE_LABEL } from "@/types/catalog";

const PAGE_SIZE = 8;

type ResultKind = "work" | "news";

interface ResultItem {
  kind: ResultKind;
  id: string;
  title: string;
  summary: string;
  cover: string;
  to: string;
  meta: string;
  score: number;
}

type FilterKey = "all" | "work" | "news";

const filterOptions: { key: FilterKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "work", label: "影视" },
  { key: "news", label: "资讯" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(query);
    setPage(1);
  }, [query]);

  useEffect(() => {
    document.title = query ? `搜索「${query}」 - 爱妻艺` : "搜索 - 爱妻艺";
  }, [query]);

  const results = useMemo<ResultItem[]>(() => {
    const key = query.trim().toLowerCase();
    if (!key) return [];

    const workResults: ResultItem[] = allWorks
      .map((work) => {
        let score = 0;
        if (work.title.toLowerCase().includes(key)) score += 10;
        if (work.genres.some((genre) => genre.toLowerCase().includes(key))) score += 5;
        if (work.director.toLowerCase().includes(key)) score += 5;
        if (work.cast.some((name) => name.toLowerCase().includes(key))) score += 4;
        if (work.tags.some((tag) => tag.toLowerCase().includes(key))) score += 3;
        if (work.description.toLowerCase().includes(key)) score += 2;
        return {
          kind: "work" as const,
          id: work.id,
          title: work.title,
          summary: work.description,
          cover: work.cover,
          to: `/detail/${work.type}/${work.id}`,
          meta: `${WORK_TYPE_LABEL[work.type]} · ${work.year} · ${work.region} · 评分 ${work.rating.toFixed(1)}`,
          score,
        };
      })
      .filter((item) => item.score > 0);

    const newsResults: ResultItem[] = newsList
      .map((item) => {
        let score = 0;
        if (item.title.toLowerCase().includes(key)) score += 10;
        if (item.tags.some((tag) => tag.toLowerCase().includes(key))) score += 4;
        if (item.category.toLowerCase().includes(key)) score += 4;
        if (item.summary.toLowerCase().includes(key)) score += 3;
        if (item.content.some((p) => p.toLowerCase().includes(key))) score += 1;
        return {
          kind: "news" as const,
          id: item.id,
          title: item.title,
          summary: item.summary,
          cover: item.cover,
          to: `/news/${item.id}`,
          meta: `${item.category} · ${item.date}`,
          score,
        };
      })
      .filter((item) => item.score > 0);

    return [...workResults, ...newsResults].sort((a, b) => b.score - a.score);
  }, [query]);

  const filtered = useMemo(
    () => (filter === "all" ? results : results.filter((item) => item.kind === filter)),
    [results, filter],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setSearchParams({ q: trimmed });
  };

  const recommendations = allWorks.filter((work) => work.featured).slice(0, 4);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <header className="mt-4">
        <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">站内搜索</h1>
        <form onSubmit={handleSearch} className="mt-4 max-w-2xl">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5 transition-colors focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              type="search"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视作品、娱乐资讯"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 md:text-base"
            />
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>
      </header>

      {query.trim() ? (
        <div className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
            <p className="text-sm text-foreground-600">
              关键词「<span className="font-semibold text-primary-600">{query}</span>」共找到{" "}
              <span className="font-semibold text-accent-600">{filtered.length}</span> 条结果
            </p>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {filterOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setFilter(option.key);
                    setPage(1);
                  }}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    filter === option.key
                      ? "bg-background-50 font-medium text-primary-600 shadow-sm"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {paged.length > 0 ? (
            <ul className="mt-5 flex flex-col gap-4">
              {paged.map((item) =>
                item.kind === "work" ? (
                  <li key={`work-${item.id}`}>
                    <Link
                      to={item.to}
                      className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                    >
                      <span className="h-28 w-20 shrink-0 overflow-hidden rounded-md bg-background-100">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 爱妻艺`}
                          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 text-xs text-accent-700">影视</span>
                          <span className="text-xs text-foreground-400">{item.meta}</span>
                        </span>
                        <span className="mt-1.5 block text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="mt-1.5 line-clamp-2 block text-sm leading-relaxed text-foreground-500">
                          {item.summary}
                        </span>
                      </span>
                    </Link>
                  </li>
                ) : (
                  <li key={`news-${item.id}`}>
                    <Link
                      to={item.to}
                      className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                    >
                      <span className="h-24 w-36 shrink-0 overflow-hidden rounded-md bg-background-100">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 爱妻艺`}
                          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-xs text-secondary-900">资讯</span>
                          <span className="text-xs text-foreground-400">{item.meta}</span>
                        </span>
                        <span className="mt-1.5 block text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="mt-1.5 line-clamp-2 block text-sm leading-relaxed text-foreground-500">
                          {item.summary}
                        </span>
                      </span>
                    </Link>
                  </li>
                ),
              )}
            </ul>
          ) : (
            <div className="mt-6">
              <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-14">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <p className="mt-4 text-base font-medium text-foreground-800">没有找到相关内容</p>
                <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或浏览下方热门推荐</p>
              </div>
              <div className="mt-8">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
                  <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                  热门推荐
                </h2>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                  {recommendations.map((work) => (
                    <WorkCard key={work.id} work={work} />
                  ))}
                </div>
              </div>
            </div>
          )}

          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </div>
      ) : (
        <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-14 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-search-line text-3xl"></i>
          </span>
          <p className="mt-4 text-base font-medium text-foreground-800">输入关键词开始搜索</p>
          <p className="mt-1 text-sm text-foreground-500">支持搜索影视作品名称、类型、演员与娱乐资讯</p>
        </div>
      )}
    </div>
  );
}